import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import { useApp } from '../contexts/AppContext';

export default function Certificate({ student, programs = [], stamps = [], awards = [] }) {
  const { t, lang } = useApp();
  const ref = useRef();

  const displayName = lang === 'ja' ? (student.name_ja || student.name) : student.name;
  const coreProgs   = programs.filter(p => p.slug !== 'daily-routine');
  const totalStamps = stamps.length;

  // Shrink the course chips as more programs are enrolled so the row(s)
  // always stay clear of the signature line and stamp below them.
  const chipScale = coreProgs.length <= 3 ? 1 : coreProgs.length <= 5 ? 0.85 : 0.7;

  // Shrink name font for longer names so they never wrap onto a second line
  // and crash into the "This certificate is proudly presented to:" text above.
  // CJK characters are ~2× the visual width of Latin ones, so count them double.
  const effectiveNameLen = [...displayName].reduce((acc, ch) =>
    acc + (/[　-鿿＀-￯぀-ヿ]/.test(ch) ? 2 : 1), 0
  );
  const nameScale = effectiveNameLen <= 14 ? 1 : effectiveNameLen <= 20 ? 0.78 : effectiveNameLen <= 28 ? 0.62 : 0.50;

  const handlePrint = async () => {
    const canvas = await html2canvas(ref.current, {
      scale: 3,
      allowTaint: true,
      useCORS: false,
      logging: false,
      onclone: (clonedDoc) => {
        const nameEl = clonedDoc.querySelector('.cert-name-overlay');
        if (nameEl) {
          nameEl.style.background = 'none';
          nameEl.style.webkitBackgroundClip = 'unset';
          nameEl.style.backgroundClip = 'unset';
          nameEl.style.webkitTextFillColor = 'unset';
          nameEl.style.color = '#1e3a8a';
        }
      },
    });
    const imgData = canvas.toDataURL('image/png');
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  @page{size:A4 landscape;margin:0}
  body{width:297mm;height:210mm;overflow:hidden}
  img{width:297mm;height:210mm;display:block}
</style>
</head><body><img src="${imgData}"/></body></html>`);
    win.document.close();
    win.onload = () => setTimeout(() => { win.print(); win.close(); }, 500);
  };

  return (
    <div className="certificate-wrapper" style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* paddingBottom trick: keeps aspect ratio 2000:1414 */}
      <div
        ref={ref}
        style={{ position: 'relative', width: '100%', paddingBottom: '70.7%' }}
      >
        <img
          src="/certificate.png"
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'block' }}
        />
        {/* absolute layer fills the whole box */}
        <div style={{ position: 'absolute', inset: 0 }}>

          {/* ── Student name (above the blue line) ── */}
          <div
            className="cert-name-overlay"
            style={{
              position: 'absolute',
              top: '49%',
              left: '25%',
              width: '40%',
              transform: 'translateY(-100%)',
              textAlign: 'center',
              fontFamily: "'Palatino Linotype','Book Antiqua',Palatino,Georgia,serif",
              fontSize: `clamp(${(1.3 * nameScale).toFixed(2)}rem, ${(3.4 * nameScale).toFixed(2)}vw, ${(2.6 * nameScale).toFixed(2)}rem)`,
              fontWeight: 700,
              lineHeight: 1.15,
              background: 'linear-gradient(135deg, #FF6B35 15%, #A855F7 85%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {displayName}
          </div>

          {/* ── Course chips — pinned between "SUMMER SCHOOL 2026" and the
                signature line, shrinking to fit as more courses are added ── */}
          {coreProgs.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '68%',
              left: '19%',
              width: '50%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: `${0.2 * chipScale}rem ${0.3 * chipScale}rem`,
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              {coreProgs.map((p, i) => (
                <span key={i} style={{
                  background: 'rgba(255,255,255,0.9)',
                  border: '1.5px solid rgba(29,78,216,0.45)',
                  borderRadius: 999,
                  padding: `${0.1 * chipScale}rem ${0.55 * chipScale}rem`,
                  fontSize: `clamp(${0.4 * chipScale}rem, ${0.7 * chipScale}vw, ${0.65 * chipScale}rem)`,
                  fontWeight: 700,
                  color: '#1D4ED8',
                  whiteSpace: 'nowrap',
                }}>
                  {p.icon && `${p.icon} `}{lang === 'ja' ? (p.name_ja || p.name) : p.name}
                </span>
              ))}
            </div>
          )}

          {/* ── Stamps badge — tilted, right side ── */}
          {totalStamps > 0 && (
            <div style={{
              position: 'absolute',
              top: '44%',
              left: '66%',
              transform: 'rotate(12deg)',
              background: 'linear-gradient(135deg,#FEF3C7,#FDE68A)',
              border: '2px solid #D97706',
              borderRadius: 999,
              padding: '0.15rem 0.6rem',
              fontSize: 'clamp(0.45rem, 0.7vw, 0.65rem)',
              fontWeight: 800,
              color: '#92400E',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}>
              ⭐ {totalStamps} {lang === 'ja' ? 'スタンプ' : 'Stamps'}
            </div>
          )}

          {/* ── Awards — tilted banners scattered ── */}
          {awards.length > 0 && awards.map((a, i) => {
            const positions = [
              { top: '79%', left: '5%',  rotate: '-8deg'  },
              { top: '12%', left: '67%', rotate: '7deg'   },
              { top: '79%', left: '68%', rotate: '6deg'   },
              { top: '12%', left: '8%',  rotate: '-6deg'  },
            ];
            const pos = positions[i % positions.length];
            return (
              <div key={i} style={{
                position: 'absolute',
                top: pos.top,
                left: pos.left,
                transform: `rotate(${pos.rotate})`,
                background: 'linear-gradient(135deg,#EDE9FE,#DDD6FE)',
                border: '1.5px solid rgba(124,58,237,0.5)',
                borderRadius: 8,
                padding: '0.12rem 0.5rem',
                fontSize: 'clamp(0.4rem, 0.6vw, 0.58rem)',
                fontWeight: 800,
                color: '#5B21B6',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
              }}>
                🏆 {lang === 'ja' ? (a.award_label_ja || a.award_label) : a.award_label}
              </div>
            );
          })}

        </div>
      </div>

      <div className="text-center mt-4 no-print">
        <button className="btn-summer" onClick={handlePrint}>
          🖨️ {t.printCertificate}
        </button>
      </div>
    </div>
  );
}
